/**
 * @fileoverview Response helpers for this feature's mock endpoints.
 * @description Builds the backend's success and error envelopes for the capabilities mocks. Never
 * imported outside this feature's mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

/**
 * @description Wraps a payload in the backend's success envelope.
 * @param {unknown} data - The payload.
 * @returns {object} The envelope.
 */
export function ok(data: unknown) {
  return { httpStatus: 200, message: 'OK', traceId: crypto.randomUUID(), data }
}

/**
 * @description Wraps one page of a list in the backend's paginated success envelope, with the
 * top-level `meta {page, limit, total}` the real list endpoints carry.
 * @param {unknown[]} items - Every matching item.
 * @param {{ page?: number; limit?: number } | undefined} params - The request's paging.
 * @returns {object} The envelope holding the requested page.
 */
export function okPage(items: unknown[], params?: { page?: number; limit?: number }) {
  const page = params?.page ?? 1
  const limit = params?.limit ?? 20
  return {
    ...ok(items.slice((page - 1) * limit, page * limit)),
    meta: { page, limit, total: items.length },
  }
}

/**
 * @description Builds the backend's error envelope.
 * @param {number} httpStatus - The HTTP status.
 * @param {string} appCode - The application error code.
 * @param {string} message - The message.
 * @returns {[number, object]} The mock reply.
 */
export function fail(httpStatus: number, appCode: string, message: string): [number, object] {
  return [
    httpStatus,
    { httpStatus, httpError: '', appCode, message, traceId: crypto.randomUUID(), data: [] },
  ]
}
